import { useEffect, useState } from "react";
import { Formik, getIn } from "formik";
import * as Yup from "yup";
import { Button, Card, Col, Row, Spinner } from "reactstrap";
import FormInput from "../../../components/FormInput";
import { addCity, getCity, updateCity } from "../../../services/city.service";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import QueryString from "qs";
import { toast } from "react-hot-toast";
import FormSelect from "../../../components/FormSelect";
import { getStates } from "../../../services/state.service";

interface State {
  id: string;
  name: string;
}

interface InitialValues {
  name: string;
  state: State | undefined;
}

const initialValues: InitialValues = {
  name: "",
  state: undefined,
};

const validationSchema = Yup.object({
  name: Yup.string().required("City name is required"),
  state: Yup.object().required("State is required"),
});

export default function Form() {
  const location = useLocation();
  const navigate = useNavigate();
  const params: any = useParams();
  const [initialValuesData, setInitialValuesData] = useState(initialValues);
  const [loader, setLoader] = useState(true);
  const query: any = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  useEffect(() => {
    if (params.id) {
      getCity(params.id)
        .then((response) => {
          const cityData = response.data;

          // Update the initialValues object with the received data
          const updatedInitialValues = {
            ...initialValues,
            name: cityData.name || "",
            state: cityData.state || undefined,
          };

          setInitialValuesData(updatedInitialValues);
          setLoader(false);
        })
        .catch((error) => {
          // Handle error if API call fails
          toast.error(`Error fetching city data: ${JSON.stringify(error)}`);
          setLoader(false);
        });
    } else {
      setLoader(false);
    }
  }, [params.id]);

  function handleSubmit(values: any, actions: any) {
    const updatedData: any = {
      ...values,
      state: values.state.id,
    };

    if (params.id) {
      updateCity(params.id, updatedData)
        .then(() => {
          toast.success("City updated successfully");
          navigate(query.redirect);
        })
        .catch(() => {
          toast.error("Failed to update city");
          actions.setSubmitting(false);
        });
    } else {
      addCity(updatedData)
        .then(() => {
          toast.success("City added successfully");
          navigate(query.redirect);
        })
        .catch(() => {
          toast.error("Failed to add city");
          actions.setSubmitting(false);
        });
    }
  }

  return (
    <div className='d-flex flex-column h-100 px-sm-1 w-100 pb-sm-1'>
      {loader ? (
        <Spinner />
      ) : (
        <Formik
          initialValues={initialValuesData}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleBlur, handleChange, setFieldValue, handleSubmit }) => (
            <>
              <Row className='align-items-center mb-2 page-title-row'>
                <Col sm>
                  <h1 className='my-1 fw-bold h4'>City Master</h1>
                </Col>
                <Col sm='auto'>
                  <div className='justify-content-between'>
                    <Button
                      size='sm'
                      color='secondary'
                      className='my-1 me-2 fw-semibold'
                      style={{ opacity: 1 }}
                      onClick={() => {
                        navigate(query.redirect);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size='sm'
                      color='primary'
                      className='my-1 text-white'
                      onClick={() => handleSubmit()}
                    >
                      Submit
                    </Button>
                  </div>
                </Col>
              </Row>
              <div className='flex-grow-1 overflow-auto'>
                <Card className='h-100 p-3 w-100 '>
                  <Col md={8}>
                    <div className='d-flex flex-column h-100 rounded-0'>
                      <form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={6}>
                            <FormInput
                              name='name'
                              label='City Name'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={getIn(values, "name")}
                              error={getIn(errors, "name")}
                              touched={getIn(touched, "name")}
                            />
                          </Col>
                          <Col md={6}>
                            <FormSelect
                              id='state'
                              name='state'
                              label='State'
                              isAsync
                              getOptions={getStates}
                              onChange={(name: any, value: any) => {
                                setFieldValue(name, value);
                              }}
                              onBlur={handleBlur}
                              value={getIn(values, "state")}
                              error={getIn(errors, "state")}
                              touched={getIn(touched, "state")}
                            />
                          </Col>
                        </Row>
                      </form>
                    </div>
                  </Col>
                </Card>
              </div>
            </>
          )}
        </Formik>
      )}
    </div>
  );
}
